import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageParts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Settings2, Boxes, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { usePermissions } from "@/lib/auth";
import EventPanel from "@/components/catalogue/EventPanel";
import ProductPanel from "@/components/catalogue/ProductPanel";
import ManagePackagesDialog from "@/components/catalogue/ManagePackagesDialog";
import { EventDialog } from "@/components/catalogue/CatalogueDialogs";

const KIND_LABEL = { workshop: "Workshop", webinar: "Webinar", campaign: "Campaign", cohort: "Cohort" };

export default function Catalogue() {
  const { can } = usePermissions();
  const canCreate = can("catalogue.create");
  const canEdit = can("catalogue.edit");
  const canDelete = can("catalogue.delete");

  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [skus, setSkus] = useState([]);
  const [offers, setOffers] = useState([]);
  const [openEvent, setOpenEvent] = useState(null); // event object
  const [openProduct, setOpenProduct] = useState(null); // product object
  const [manageOpen, setManageOpen] = useState(false);
  const [newEventOpen, setNewEventOpen] = useState(false);

  const loadAll = async () => {
    const [e, p, s, o] = await Promise.all([
      api.get("/catalogue/events"), api.get("/catalogue/products"),
      api.get("/catalogue/skus"), api.get("/catalogue/offers"),
    ]);
    setEvents(e.data); setProducts(p.data); setSkus(s.data); setOffers(o.data);
  };
  useEffect(() => { loadAll(); }, []);

  const offersByEvent = useMemo(() => {
    const m = {};
    for (const o of offers) { (m[o.event_id] = m[o.event_id] || []).push(o); }
    return m;
  }, [offers]);

  const productStats = useMemo(() => {
    const bySkuProduct = {};
    for (const s of skus) { (bySkuProduct[s.product_id] = bySkuProduct[s.product_id] || []).push(s); }
    const eventsBySkuIds = (skuIds) => new Set(offers.filter((o) => skuIds.includes(o.sku_id)).map((o) => o.event_id));
    return products.reduce((acc, p) => {
      const productSkus = bySkuProduct[p.id] || [];
      acc[p.id] = {
        packages_count: productSkus.length,
        events_count: eventsBySkuIds(productSkus.map((s) => s.id)).size,
      };
      return acc;
    }, {});
  }, [products, skus, offers]);

  return (
    <>
      <PageHeader
        title="Catalogue"
        subtitle="Manage events, products and the packages offered inside each event."
      >
        <Button variant="outline" size="sm" onClick={() => setManageOpen(true)} data-testid="manage-packages-btn">
          <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Manage Products & Packages
        </Button>
        {canCreate && (
          <Button size="sm" onClick={() => setNewEventOpen(true)} data-testid="new-event-button">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New event
          </Button>
        )}
      </PageHeader>

      <PageBody>
        <Tabs defaultValue="events">
          <TabsList className="mb-4">
            <TabsTrigger value="events" data-testid="tab-events">
              <CalendarDays className="w-3.5 h-3.5 mr-1.5" /> Events
            </TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">
              <Boxes className="w-3.5 h-3.5 mr-1.5" /> Products
            </TabsTrigger>
          </TabsList>

          <TabsContent value="events">
            <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead className="w-32">Event code</TableHead>
                    <TableHead>Event name</TableHead>
                    <TableHead className="w-28">Kind</TableHead>
                    <TableHead className="w-28">Start</TableHead>
                    <TableHead className="w-28">End</TableHead>
                    <TableHead className="w-28 text-center">Packages</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No events yet.</TableCell></TableRow>}
                  {events.map((e) => (
                    <TableRow
                      key={e.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => setOpenEvent(e)}
                      data-testid={`event-row-${e.code}`}
                    >
                      <TableCell className="font-mono text-xs">{e.code}</TableCell>
                      <TableCell className="font-medium">{e.name}</TableCell>
                      <TableCell className="text-xs capitalize">{KIND_LABEL[e.kind] || e.kind}</TableCell>
                      <TableCell className="text-xs font-mono text-slate-600">{e.start_date || "—"}</TableCell>
                      <TableCell className="text-xs font-mono text-slate-600">{e.end_date || "—"}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="font-mono">{e.packages_count ?? (offersByEvent[e.id] || []).length}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="products">
            <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead className="w-32">Code</TableHead>
                    <TableHead>Product name</TableHead>
                    <TableHead className="w-40 text-center">Packages</TableHead>
                    <TableHead className="w-40 text-center">Events offered in</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-8">No products yet — use Manage Products & Packages to add one.</TableCell></TableRow>}
                  {products.map((p) => (
                    <TableRow
                      key={p.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => setOpenProduct(p)}
                      data-testid={`product-row-${p.code}`}
                    >
                      <TableCell className="font-mono text-xs">{p.code}</TableCell>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="font-mono">{productStats[p.id]?.packages_count ?? 0}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="font-mono">{productStats[p.id]?.events_count ?? 0}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </PageBody>

      <EventPanel
        event={openEvent}
        onClose={() => setOpenEvent(null)}
        products={products}
        skus={skus}
        offers={offersByEvent[openEvent?.id] || []}
        onChange={loadAll}
        canEdit={canEdit}
        canDelete={canDelete}
      />
      <ProductPanel
        product={openProduct}
        onClose={() => setOpenProduct(null)}
        onChange={loadAll}
        skus={skus.filter((s) => s.product_id === openProduct?.id)}
        canEdit={canEdit}
        canDelete={canDelete}
      />
      <ManagePackagesDialog
        open={manageOpen}
        onOpenChange={setManageOpen}
        products={products}
        skus={skus}
        onChange={loadAll}
        canCreate={canCreate}
        canEdit={canEdit}
        canDelete={canDelete}
      />
      <EventDialog
        open={newEventOpen}
        onOpenChange={setNewEventOpen}
        onDone={() => { setNewEventOpen(false); loadAll(); toast.success("Event created"); }}
      />
    </>
  );
}
